import { Fragment, memo, type ReactNode, useEffect, useMemo } from "react";
import { useTheme, useThemeFlags } from "src/hooks";
import { type ICol, type ISection } from "src/interfaces";
import { type IReportEntry } from "src/pages/Reports/Report/Report.types";
import { useDispatch, useSelector } from "src/redux/hooks";

import { setIsEmpty } from "../../../redux/slices/entriesGeneratorSlice";
import { getChangedChecklists } from "../../../utils/logbookUtils/getChangedChecklists";
import { useShowContent } from "../../../generator/hooks/useShowContent";
import Attachments from "./Attachments/MixedEntry/Attachments";
import { useGetAttachments } from "./Attachments/useGetAttachments";
import CheckLists from "./CheckLists/CheckLists";
import LandscapeSection from "./Section/LandscapeSection/LandscapeSection";
import Section from "./Section/Section/Section";
import VerifiedSignature from "./Signature/VerifiedSignature";

interface Props {
  entry: IReportEntry;
  renderTitle?: () => ReactNode;
  entryIndex?: number;
  pdfEntry?: boolean;
}

const Entry = ({ entry, renderTitle, entryIndex, pdfEntry }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme() as any;
  const flags = useThemeFlags();
  const { shouldHideElement } = useShowContent();
  const { uppercase } = useSelector(state => state.preferencesSlice);
  const reportMode = useSelector(
    state => state.entriesGeneratorSlice.reportMode,
  );
  const wantsAttachments =
    reportMode === "attachments" ||
    reportMode === "entriesAndAttachments" ||
    reportMode === "all";
  const attachments = useGetAttachments(entry.lbeid, wantsAttachments);

  if (!entry.lbentry?.sections) {
    return <></>;
  }
  const checkLists = useMemo(
    () =>
      entry.lbentry.checklists ? Object.values(entry.lbentry.checklists) : [],
    [entry],
  );

  const parsedCheckLists = useMemo(
    () =>
      getChangedChecklists(
        checkLists.map(checklist => {
          const list = Array.isArray(checklist.data)
            ? checklist.data
            : checklist.data.type === "single"
              ? checklist.data.list
              : checklist.data.list.map(el => ({
                name: el.name,
                items: (el as any).items,
              }));
          return {
            list,
            name: checklist.name,
            description: checklist.description,
            version: checklist.version,
          };
        }),
      ),
    [checkLists],
  );

  const showChecklists =
    !flags.disableChecklists &&
    (reportMode === "checkLists" ||
      reportMode === "entriesAndCheckLists" ||
      reportMode === "all") &&
    parsedCheckLists &&
    parsedCheckLists.length > 0;
  const showAttachments =
    (reportMode === "attachments" ||
      reportMode === "entriesAndAttachments" ||
      reportMode === "all") &&
    attachments &&
    attachments.length > 0;

  const showEntries =
    reportMode !== "checkLists" && reportMode !== "attachments";

  const hasVisibleContent = showEntries || showChecklists || showAttachments;

  useEffect(() => {
    if (hasVisibleContent) dispatch(setIsEmpty(false));
  }, [dispatch, hasVisibleContent]);

  if (!hasVisibleContent) return null;
  return (
    <div
      className="entry"
      data-lbid={entry.lbid}
      style={{
        marginBottom: "50px",
        border: `1px solid ${theme.colors.main[9]}`,
        paddingBottom: "18px",
        width: "100%",
      }}>
      {renderTitle && (
        <div
          className="entryName"
          style={{
            display: "flex",
            fontWeight: "bold",
            fontSize: "20px",
            color: theme.colors.thColor[0],
            backgroundColor: theme.colors.thBackgroundColor[0],
            padding: "18px",
          }}>
          {renderTitle()}
        </div>
      )}
      {entry.reason && (
        <div style={{ paddingLeft: "18px" }}>
          <p style={{ margin: "5px 0" }}>Reason for withdrawal: </p>
          <p
            style={{
              margin: "0",
              marginLeft: "18px",
              textTransform: uppercase ? "uppercase" : "none",
            }}>
            {entry.reason}
          </p>
        </div>
      )}

      {showEntries && (
        <div
          className="entryContent"
          style={{
            margin: "18px",
            overflowX: "auto",
            display: "grid",
            gridTemplateColumns: "minmax(min-content, 1fr)",
          }}>
          {entry.lbentry.sections.map((section: ISection, index: number) => {
            // Check if this section should be hidden
            if (shouldHideElement(section, entryIndex)) {
              return null;
            }

            // Build history data
            const history: Array<ICol[][]> = [];
            if (entry.history) {
              entry.history.forEach((h: IReportEntry) => {
                if (h.lbentry.sections[index]?.rows) {
                  history.push(h.lbentry.sections[index].rows as any);
                }
              });
            }

            // Extract deleted rows for this section
            const deletedRows =
              (entry as any).deletedRows?.filter(
                (deletedRow: any) => deletedRow.data?.sectionIndex === index,
              ) || [];
            if (section.subSections) {
              return (
                <Fragment key={`frag-sub-${index}`}>
                  {section.subSections.map((subSection, subIndex) => (
                    <LandscapeSection
                      key={`landscape-section-${subIndex}`}
                      section={subSection}
                      historyRows={history}
                    />
                  ))}
                </Fragment>
              );
            }
            return (
              <Section
                key={index}
                section={section}
                historyRows={history}
                deletedRows={deletedRows}
                lbeid={entry.lbeid}
                sIndex={index}
                entryIndex={entryIndex}
                pdfEntry={pdfEntry}
              />
            );
          })}
        </div>
      )}

      {showChecklists && <CheckLists checkLists={parsedCheckLists} />}

      {showAttachments && <Attachments files={attachments} />}

      {(showEntries || showChecklists) && (
        <div style={{ display: "flex", gap: "10px" }}>
          {entry.idata && (
            <VerifiedSignature
              idata={entry.idata}
              title="Verified Signature:"
            />
          )}
          {entry.secdata && (
            <VerifiedSignature
              idata={entry.secdata}
              title="Co Signed Signature:"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default memo(Entry);
